export type MoneyUnit = "$" | "¢";

// Object pool for referential equality
const moneyPool = new Map<number, Money>();

const UsDollar = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

function roundCentsUp(value: number): number {
  if (Number.isInteger(value)) return value;
  return value >= 0 ? Math.ceil(value) : Math.floor(value);
}

export class Money {
  private constructor(private cents: number) {}

  private static addToMap(cents: number): Money {
    const value = new Money(cents);
    moneyPool.set(cents, value);
    return value;
  }

  static from(value: number, unit: MoneyUnit) {
    const cents = unit === "¢" ? Math.round(value) : Math.round(value * 100);
    return moneyPool.get(cents) ?? Money.addToMap(cents);
  }

  static fromCentsRoundedUp(valueInCents: number) {
    const cents = roundCentsUp(valueInCents);
    return moneyPool.get(cents) ?? Money.addToMap(cents);
  }

  static parse(input: `$${number}` | `${number}¢`) {
    if (input[0] === "$") {
      return Money.from(+input.substring(1), "$");
    } else {
      return Money.from(+input.substring(0, input.length - 1), "¢");
    }
  }

  static get zero() {
    return Money.from(0, "¢");
  }

  asDollars() {
    return this.cents / 100;
  }

  asCents() {
    return this.cents;
  }

  format() {
    return this.cents == 0 ? "FREE" : UsDollar.format(this.asDollars());
  }

  mul(rhs: number) {
    return Money.from(this.cents * rhs, "¢");
  }

  add(rhs: Money) {
    return Money.from(this.cents + rhs.cents, "¢");
  }

  sub(rhs: Money) {
    return Money.from(this.cents - rhs.cents, "¢");
  }

  max(rhs: Money) {
    return this >= rhs ? this : rhs;
  }

  valueOf() {
    return this.asCents();
  }
}

/** So $`5` will turn into Money.from(5, "$") */
export const $ = (strings: TemplateStringsArray) =>
  Money.from(+strings[0], "$");
